import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  ApiConversation,
  ApiResponse,
  Booth,
  ChatMessage,
  JoinSessionData,
  PostChatMessage,
  Registration,
  Tenant,
  Venue,
  VenueArchiveLayout,
  VenueSession,
  VenueSessionStatus,
  WolUser,
} from "../../app/types";
import { AuthState } from "../auth/authSlice";

const useStaging = process.env.REACT_APP_MOLO_DEV === "staging";
const baseUrl = useStaging
  ? "https://staging.workoutloud.com/api/v1/"
  : "/api/v1/";
const hardCodedAccessToken = useStaging
  ? { access_token: "presenter" }
  : undefined;

const api = createApi({
  reducerPath: "api",
  tagTypes: ["Sessions"],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const { access_token } =
        hardCodedAccessToken ?? (getState() as { auth: AuthState }).auth;
      headers.set("Authorization", `Bearer ${access_token}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getTenant: builder.query<Tenant, boolean>({
      query: () => ({
        url: "me/tenant",
      }),
      transformResponse: (response) =>
        (response as ApiResponse<Tenant>).results,
    }),
    getUser: builder.query<WolUser, boolean>({
      query: () => ({
        url: "me",
      }),
      transformResponse: (response) =>
        (response as ApiResponse<WolUser[]>).results[0],
    }),
    getVenue: builder.query<Venue, string>({
      query: (id) => ({
        url: `venues/${id}`,
      }),
      transformResponse: (response) =>
        (response as ApiResponse<Venue[]>).results[0],
    }),
    getSessions: builder.query<VenueSession[], string>({
      query: (id) => ({
        url: `venues/${id}/venue_sessions`,
      }),
      transformResponse: (response) =>
        (response as ApiResponse<VenueSession[]>).results,
      providesTags: ["Sessions"],
    }),
    getBooths: builder.query<Booth[], string>({
      query: (id) => ({
        url: `venues/${id}/booths`,
      }),
      transformResponse: (response) =>
        (response as ApiResponse<Booth[]>).results,
    }),
    getRegistration: builder.query<Registration, number | string>({
      query: (venueId) => ({
        url: `venues/${venueId}/registration`,
      }),
    }),
    joinSession: builder.query<JoinSessionData, number>({
      query: (id) => ({
        url: `venue_sessions/${id}/join`,
        method: "post",
      }),
      transformResponse: (response) =>
        (response as ApiResponse<JoinSessionData>).results,
    }),
    updateSessionLayout: builder.mutation<
      void,
      { sessionId: number; layout: VenueArchiveLayout }
    >({
      query: ({ sessionId, layout }) => ({
        url: `venue_sessions/${sessionId}`,
        method: "post",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: { layout },
      }),
    }),
    updateSessionState: builder.mutation<
      void,
      {
        status: VenueSessionStatus;
        sessionId: number;
        venueId: string;
        layout: VenueArchiveLayout;
      }
    >({
      query: ({ sessionId, status, layout }) => ({
        url: `venue_sessions/${sessionId}`,
        method: "post",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: { status, layout },
      }),
      invalidatesTags: ["Sessions"],
      async onQueryStarted(
        { sessionId, status, venueId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          api.util.updateQueryData("getSessions", venueId, (draft) => {
            const draftSession = draft.find(
              (item) => item.sessionId === sessionId,
            );
            if (draftSession) {
              draftSession.status = status;
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getConversation: builder.query<ApiConversation, string>({
      query: (topic) => ({
        url: `conversations/${topic}?limit=100`,
      }),
    }),
    postChatMessage: builder.mutation<ChatMessage, PostChatMessage>({
      query: ({ topic, message, isQuestion }) => ({
        url: `conversations/${topic}/messages`,
        method: "post",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: { message, isQuestion },
      }),
    }),
    answerQuestion: builder.mutation<
      ChatMessage,
      { topic: string; messageId: number; isAnswered: boolean }
    >({
      query: ({ topic, messageId, isAnswered }) => ({
        url: `conversations/${topic}/messages/${messageId}`,
        method: "patch",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: { isAnswered },
      }),
    }),
  }),
});

export default api;

export const {
  usePostChatMessageMutation,
  useAnswerQuestionMutation,
  useUpdateSessionStateMutation,
  useUpdateSessionLayoutMutation,
} = api;
